import { useMemo } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

import { Icon } from '../../shared/Icon';
import { GiftScheduleGiftTile } from './GiftScheduleGiftTile';
import { PlannedGiftTile } from './PlannedGiftTile';

import type { GameElementDto } from '../../hooks/gameElements';
import type { JiraSubtask } from '../../hooks/useJiraApi';

import { getCalendarDateParts, parseDate } from '../utils/dates';

import type { ScheduledGiftOccurrence } from '../domain/giftSchedule';

interface GiftScheduleMonthCellProps {
    year: number;
    month: number;
    gifts: ScheduledGiftOccurrence[];
    plannedGifts: JiraSubtask[];
    gameElements: Map<string, GameElementDto>;
    onSelectGift: (occurrence: ScheduledGiftOccurrence) => void;
    onSelectPlanned: (subtask: JiraSubtask) => void;
}

export const GiftScheduleMonthCell = ({
    year,
    month,
    gifts,
    plannedGifts,
    gameElements,
    onSelectGift,
    onSelectPlanned,
}: GiftScheduleMonthCellProps) => {
    const total = gifts.length + plannedGifts.length;
    const isEmpty = total === 0;
    const monthName = new Intl.DateTimeFormat(undefined, {
        month: 'short',
    }).format(new Date(2000, month));
    const fridays = useMemo(() => getFridays(month, year), [month, year]);
    const weeks = useMemo(
        () =>
            fridays.map((friday) => {
                const weekStart = new Date(friday);
                weekStart.setDate(weekStart.getDate() - 4);
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 7);
                const weekGifts = gifts.filter((gift) =>
                    isInWeek(gift.startDate, weekStart, weekEnd),
                );
                const weekPlanned = plannedGifts.filter((gift) =>
                    isInWeek(gift.startTime, weekStart, weekEnd),
                );
                const scheduled = weekGifts.some((gift) =>
                    startsOnDate(gift.startDate, friday),
                );
                const planned =
                    !scheduled &&
                    plannedGifts.some((gift) =>
                        startsOnDate(gift.startTime, friday),
                    );

                return { friday, weekGifts, weekPlanned, scheduled, planned };
            }),
        [fridays, gifts, plannedGifts],
    );

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: isEmpty ? 'action.hover' : 'background.paper',
            }}
        >
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    alignItems: 'baseline',
                    px: 1.25,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 700,
                        color: isEmpty ? 'text.disabled' : 'text.primary',
                    }}
                >
                    {monthName}
                </Typography>
                {!isEmpty && (
                    <Typography variant="caption" color="text.secondary">
                        {total}
                    </Typography>
                )}
            </Stack>
            <Stack sx={{ p: 1, gap: 1 }}>
                {weeks.map(
                    ({
                        friday,
                        weekGifts,
                        weekPlanned,
                        scheduled,
                        planned,
                    }) => {
                        const status = scheduled
                            ? 'Scheduled'
                            : planned
                              ? 'Planned'
                              : 'Missing';

                        return (
                            <Box key={friday.toISOString()}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{
                                            minWidth: 58,
                                            textTransform: 'uppercase',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Week of {formatWeekStart(friday)}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        title={`${status}: Friday ${friday.getDate()}`}
                                        aria-label={`${status}: Friday ${friday.getDate()}`}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.25,
                                            fontWeight: 600,
                                            color: scheduled
                                                ? 'success.main'
                                                : planned
                                                  ? 'warning.main'
                                                  : 'error.main',
                                        }}
                                    >
                                        <Icon
                                            name={
                                                scheduled
                                                    ? 'check_circle'
                                                    : planned
                                                      ? 'schedule'
                                                      : 'warning'
                                            }
                                        />
                                        Fri {friday.getDate()}
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={0.75}
                                    sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
                                >
                                    {weekGifts.map((occurrence) => (
                                        <GiftScheduleGiftTile
                                            key={`${occurrence.gift.id}:${occurrence.publicationId}`}
                                            occurrence={occurrence}
                                            gameElements={gameElements}
                                            onSelect={onSelectGift}
                                        />
                                    ))}
                                    {weekPlanned.map((subtask) => (
                                        <PlannedGiftTile
                                            key={subtask.key}
                                            subtask={subtask}
                                            status="planned"
                                            onSelect={onSelectPlanned}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        );
                    },
                )}
            </Stack>
        </Paper>
    );
};

const formatWeekStart = (friday: Date) => {
    const monday = new Date(friday);
    monday.setDate(monday.getDate() - 4);
    return `${monday.toLocaleString(undefined, { month: 'short' })} ${monday.getDate()}`;
};

const isInWeek = (value: string | null | undefined, start: Date, end: Date) => {
    const date = value ? parseDate(value) : null;
    return (
        !!date && !Number.isNaN(date.getTime()) && date >= start && date < end
    );
};

const getFridays = (month: number, year: number) => {
    const first = new Date(year, month, 1);
    const dayOffset = (5 - first.getDay() + 7) % 7;
    const fridays: Date[] = [];

    for (
        let day = 1 + dayOffset;
        day <= new Date(year, month + 1, 0).getDate();
        day += 7
    ) {
        fridays.push(new Date(year, month, day));
    }

    return fridays;
};

const startsOnDate = (value: string | null | undefined, date: Date) => {
    if (!value) {
        return false;
    }

    const parts = getCalendarDateParts(value);
    if (!parts) return false;
    const parsed = parseDate(value);
    if (!parsed) return false;
    return (
        parts.year === date.getFullYear() &&
        parts.month === date.getMonth() &&
        parsed.getDate() === date.getDate()
    );
};
